/**
 * Constants for SmswiserCampaignStatsTab.
 * For Media Devoted | Created By Ciro Autiero
 */

/** First column width (drop list, list, gateway) – same for all stats tabs. */
export const FIRST_COLUMN_WIDTH_PX = 280;

/** Columns matching official Voluum dashboard. */
export const VOLUUM_COLUMNS = [
  { key: 'impressions', label: 'Impressions', format: 'int' as const },
  { key: 'ImCTR', label: 'SMS CTR', format: 'percent' as const, colorHint: 'metric' as const },
  { key: 'uniqueVisits', label: 'Unique visits', format: 'int' as const },
  { key: 'uniqueClicks', label: 'Unique clicks', format: 'int' as const },
  { key: 'uCTR', label: 'uCTR', format: 'percent' as const, colorHint: 'metric' as const },
  { key: 'Click2Reg', label: 'Click2Reg', format: 'percent' as const, colorHint: 'metric' as const },
  { key: 'conversions', label: 'Conversions', format: 'int' as const, colorHint: 'metric' as const, columnAccent: 'conv' as const },
  { key: 'Reg2Dep', label: 'Reg2Dep', format: 'percent' as const, colorHint: 'metric' as const },
  { key: 'customConversions6', label: 'REG', format: 'int' as const, colorHint: 'metric' as const, columnAccent: 'reg' as const },
  { key: 'revenue', label: 'Revenue', format: 'currency' as const, colorHint: 'money' as const },
  { key: 'customConversions7', label: 'FTD', format: 'int' as const, colorHint: 'metric' as const, columnAccent: 'ftd' as const },
  { key: 'cost', label: 'Cost', format: 'currency' as const, colorHint: 'negative' as const },
  { key: 'profit', label: 'Profit', format: 'currency' as const, colorHint: 'money' as const },
  { key: 'roi', label: 'ROI', format: 'percent' as const, colorHint: 'money' as const },
  { key: 'uEPC', label: 'uEPC', format: 'decimal' as const, colorHint: 'money' as const },
  { key: 'uEPV', label: 'uEPV', format: 'decimal' as const, colorHint: 'money' as const },
  { key: 'epv', label: 'EPV', format: 'currency' as const, colorHint: 'money' as const },
  { key: 'epc', label: 'EPC', format: 'currency' as const, colorHint: 'money' as const },
  { key: 'epl', label: 'EPL', format: 'currency' as const, colorHint: 'money' as const },
  { key: 'CPuC', label: 'CPuC', format: 'decimal' as const, colorHint: 'negative' as const },
  { key: 'CPL', label: 'CPL', format: 'decimal' as const, colorHint: 'negative' as const },
  { key: 'CPS', label: 'CPS', format: 'decimal' as const, colorHint: 'negative' as const },
];

/** Optional `tooltip` lets callers (e.g. the SMSWiser tool stats) attach an "i" hint to a header. */
export type ColumnDef = (typeof VOLUUM_COLUMNS)[number] & { tooltip?: string };

export const LENGTH_OPTIONS = [100, 200, 300, 500, 800, 1000] as const;

export const FILTER_FIELDS = [
  { key: 'leadList', label: 'Drop list / title', type: 'text' as const },
  { key: 'staticTag', label: 'Static tag', type: 'text' as const },
  { key: 'tags', label: 'Performance tags', type: 'text' as const },
  ...VOLUUM_COLUMNS.map((c) => ({ key: c.key, label: c.label, type: 'numeric' as const })),
];

/** Filter fields for lists dashboard (first column = list name/ID). */
export const FILTER_FIELDS_LISTS = [
  { key: 'list', label: 'List / ID', type: 'text' as const },
  { key: 'tags', label: 'Tags', type: 'text' as const },
  ...VOLUUM_COLUMNS.map((c) => ({ key: c.key, label: c.label, type: 'numeric' as const })),
];

export const NUMERIC_OPERATORS = [
  { op: '>=', label: '≥' },
  { op: '<=', label: '≤' },
  { op: '>', label: '>' },
  { op: '<', label: '<' },
  { op: '=', label: '=' },
] as const;

export {
  BINOM_DYNAMIC_COLUMN_PREFIX_BY_DASHBOARD_KEY,
  BINOM_PLAIN_FIELD_BY_DASHBOARD_KEY,
  VOLUUM_METRIC_ALIASES,
} from './metric-aliases';

export const GROUP_BY_OPTIONS = [
  { key: 'none', label: 'No grouping' },
  { key: 'campaign', label: 'Campaign' },
  { key: 'leadsList', label: 'Leads list' },
  { key: 'gateway', label: 'Gateway' },
  { key: 'gateway_leadsList', label: 'Gateway → Leads list' },
  { key: 'leadsList_gateway', label: 'Leads list → Gateway' },
] as const;

/** Group by for lists – "none" plus the reversible List ▸ Source tree. */
export const GROUP_BY_OPTIONS_LISTS = [
  { key: 'none', label: 'No grouping' },
  { key: 'list_source', label: 'List ▸ Source' },
] as const;

/** Filter fields for gateways dashboard (first column = gateway ID/name). */
export const FILTER_FIELDS_GATEWAYS = [
  { key: 'gateway', label: 'Gateway / ID', type: 'text' as const },
  { key: 'tags', label: 'Tags', type: 'text' as const },
  ...VOLUUM_COLUMNS.map((c) => ({ key: c.key, label: c.label, type: 'numeric' as const })),
];

/** Group by for gateways – "none" plus reversible Gateway ▸ Carrier / Carrier ▸ Gateway trees. */
export const GROUP_BY_OPTIONS_GATEWAYS = [
  { key: 'none', label: 'No grouping' },
  { key: 'gateway_carrier', label: 'Gateway ▸ Mobile carrier' },
  { key: 'carrier_gateway', label: 'Mobile carrier ▸ Gateway' },
] as const;

/** Filter fields for sources dashboard (first column = source ID/name). */
export const FILTER_FIELDS_SOURCES = [
  { key: 'source', label: 'Source / ID', type: 'text' as const },
  { key: 'tags', label: 'Performance tags', type: 'text' as const },
  ...VOLUUM_COLUMNS.map((c) => ({ key: c.key, label: c.label, type: 'numeric' as const })),
];

/** Group by for sources – "none" plus reversible Source ▸ Carrier / Source ▸ List trees. */
export const GROUP_BY_OPTIONS_SOURCES = [
  { key: 'none', label: 'No grouping' },
  { key: 'source_carrier', label: 'Source ▸ Mobile carrier' },
  { key: 'carrier_source', label: 'Mobile carrier ▸ Source' },
  { key: 'source_list', label: 'Source ▸ Leads list' },
] as const;

/** Filter fields for phone dashboard (first column = phone | source_id). */
export const FILTER_FIELDS_PHONE = [
  { key: 'phone', label: 'Phone / Source ID', type: 'text' as const },
  ...VOLUUM_COLUMNS.map((c) => ({ key: c.key, label: c.label, type: 'numeric' as const })),
];

/** Group by for phone – only "none" (each row is phone+source). */
export const GROUP_BY_OPTIONS_PHONE = [
  { key: 'none', label: 'No grouping' },
] as const;

/** Filter fields for texts dashboard (first column = SMS text content). */
export const FILTER_FIELDS_TEXTS = [
  { key: 'text', label: 'Text / ID', type: 'text' as const },
  { key: 'tags', label: 'Tags', type: 'text' as const },
  ...VOLUUM_COLUMNS.map((c) => ({ key: c.key, label: c.label, type: 'numeric' as const })),
];

/** Group by for texts – text (merge by ID), gateway, drop, or none (flat list). */
export const GROUP_BY_OPTIONS_TEXTS = [
  { key: 'text', label: 'By text (merge by ID)' },
  { key: 'aiTextGroup', label: 'Text group' },
  { key: 'aiTextRole', label: 'Text prompt' },
  { key: 'gateway', label: 'Gateway' },
  { key: 'drop', label: 'Drop' },
  { key: 'none', label: 'No grouping' },
] as const;

/** Filter fields for paths dashboard (first column = path ID/name). */
export const FILTER_FIELDS_PATHS = [
  { key: 'path', label: 'Path / ID', type: 'text' as const },
  { key: 'tags', label: 'Tags', type: 'text' as const },
  ...VOLUUM_COLUMNS.map((c) => ({ key: c.key, label: c.label, type: 'numeric' as const })),
];

/** Group by for paths – only "none". */
export const GROUP_BY_OPTIONS_PATHS = [{ key: 'none', label: 'No grouping' }] as const;

/** Filter fields for landers dashboard (first column = lander ID/name). */
export const FILTER_FIELDS_LANDERS = [
  { key: 'lander', label: 'Lander / ID', type: 'text' as const },
  { key: 'tags', label: 'Tags', type: 'text' as const },
  ...VOLUUM_COLUMNS.map((c) => ({ key: c.key, label: c.label, type: 'numeric' as const })),
];

/** Group by for landers – only "none". */
export const GROUP_BY_OPTIONS_LANDERS = [{ key: 'none', label: 'No grouping' }] as const;

/** Filter fields for offers dashboard (first column = offer ID/name). */
export const FILTER_FIELDS_OFFERS = [
  { key: 'offer', label: 'Offer / ID', type: 'text' as const },
  { key: 'tags', label: 'Tags', type: 'text' as const },
  ...VOLUUM_COLUMNS.map((c) => ({ key: c.key, label: c.label, type: 'numeric' as const })),
];

/** Group by for offers – only "none". */
export const GROUP_BY_OPTIONS_OFFERS = [{ key: 'none', label: 'No grouping' }] as const;

export type GroupByKey =
  | (typeof GROUP_BY_OPTIONS)[number]['key']
  | (typeof GROUP_BY_OPTIONS_TEXTS)[number]['key']
  | (typeof GROUP_BY_OPTIONS_SOURCES)[number]['key']
  | (typeof GROUP_BY_OPTIONS_LISTS)[number]['key']
  | (typeof GROUP_BY_OPTIONS_GATEWAYS)[number]['key'];

/**
 * Reversible 2-level (tree) group-by keys. Each is a `primary_secondary` pair sent
 * to the backend as `cross`; the report is fetched in that order so totals match
 * the panel. Reversing the order is a refetch (new pair), not a client-side flip.
 */
export const CROSS_GROUP_BY_KEYS = [
  'source_carrier',
  'carrier_source',
  'source_list',
  'list_source',
  'gateway_carrier',
  'carrier_gateway',
] as const;

export type CrossGroupByKey = (typeof CROSS_GROUP_BY_KEYS)[number];

export function isCrossGroupBy(groupBy: string): groupBy is CrossGroupByKey {
  return (CROSS_GROUP_BY_KEYS as readonly string[]).includes(groupBy);
}

/** The cross pair to request for a group-by (the key itself), or null if not a cross. */
export function crossPairForGroupBy(groupBy: string): CrossGroupByKey | null {
  return isCrossGroupBy(groupBy) ? groupBy : null;
}
